import React from 'react';
import styled from 'styled-components';
import BackButton from '../ui/buttons/BackButton/BackButton';
import { useTranslation } from 'react-i18next';
import { Submission } from '../../store/submission/types';
import { ReviewerTableRow } from '../../store/bid/types';
import { formatKeywordColumn, getSubmissionRow } from '../../helpers/table';
import { Role } from '../../store/conference/types';
import PrevNextButtonsCtrl from '../PrevNextButtonsCtrl/PrevNextButtonsCtrl';
import { FilterIdsKey } from '../../store/table/types';

type HeaderContent = {
  id: string;
  title: string;
  row1: {
    title: string;
    content: string | string[];
  };
  row2: {
    title: string;
    content: string | string[];
  };
  row3: {
    title: string;
    content: string | string[];
  };
  row4: {
    title: string;
    content: string | string[];
  };
};

interface Props {
  onBack: () => void;
  onMove: (rowId: number) => void;
  type: FilterIdsKey;
  row: Submission | ReviewerTableRow;
  keywordById: { [key: number]: Keyword };
  roleById: { [key: string]: Role };
}

const TableHeader: React.FC<Props> = ({ onBack, onMove, type, row, keywordById, roleById }) => {
  const { t, i18n } = useTranslation();
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  /* Generate header */
  let headerContent: HeaderContent;
  switch (type) {
    case 'App\\Models\\Submission':
      const submission = getSubmissionRow(row as Submission, true, keywordById, t);
      headerContent = {
        id: `#${submission.external_id}`,
        title: submission.title,
        row1: {
          title: t('Authors'),
          // @ts-ignore
          content: submission.authors_str,
        },
        row2: {
          title: t('Main area'),
          // @ts-ignore
          content: submission.keyword_ids.length > 0 ? submission.main_area : t('Not defined'),
        },
        row3: {
          title: t('Keywords'),
          content:
            submission.keyword_ids.length > 0
              ? submission.keyword_ids.map((id) => keywordById[id].name).join(', ')
              : t('Not defined'),
        },
        row4: { title: t('Abstract'), content: submission.abstract ? submission.abstract : t('Not defined') },
      };
      break;
    case 'App\\Models\\Person':
    case 'reviewers_to_reviewers':
      const reviewer = row as ReviewerTableRow;
      formatKeywordColumn(reviewer, keywordById);
      headerContent = {
        id: '',
        title: reviewer.full_name ?? '',
        row1: {
          title: t('Role'),
          content: roleById[reviewer.role_id].description,
        },
        row2: {
          title: t('Email'),
          content: reviewer?.email ?? '',
        },
        row3: {
          title: t('Main area'),
          // @ts-ignore
          content: reviewer.keyword_ids.length > 0 ? reviewer.main_area : t('Not defined'),
        },
        row4: {
          title: t('Keywords'),
          content:
            reviewer.keyword_ids.length > 0
              ? reviewer.keyword_ids.map((id) => keywordById[id].name).join(', ')
              : t('Not defined'),
        },
      };
      break;
    default:
      headerContent = {
        id: '',
        title: '',
        row1: {
          title: '',
          content: '',
        },
        row2: {
          title: '',
          content: '',
        },
        row3: {
          title: '',
          content: '',
        },
        row4: { title: '', content: '' },
      };
  }

  return (
    <>
      <div className="flex items-center">
        <BackButton onClick={onBack} />
        <PrevNextButtonsCtrl
          rowId={row.id}
          type={type}
          onMove={onMove}
          className={`ml-6 ${type != 'App\\Models\\Submission' ? 'gap-x-4' : ''}`}
        >
          <div className="flex items-center gap-x-3">
            {type == 'App\\Models\\Submission' ? (
              <div className="font-medium ml-1" style={{ fontSize: '0.875rem' }}>
                {headerContent.id}
              </div>
            ) : (
              <div className="text-xl font-bold">{headerContent.title}</div>
            )}
            <div className="flex items-center justify-center">
              <StyledButton className="" onClick={() => setIsCollapsed(!isCollapsed)}>
                <span>{t('Details')}</span>
              </StyledButton>
            </div>
          </div>
        </PrevNextButtonsCtrl>
        {type == 'App\\Models\\Submission' && <div className="ml-4 text-xl font-bold">{headerContent.title}</div>}
      </div>

      {!isCollapsed && (
        <div className="flex flex-row mt-3 text-sm">
          <StyledHeaderTextWrapper className="w-2/4 pr-4">
            <div>
              <span className="font-bold">{headerContent.row1.title}:</span> {headerContent.row1.content}
            </div>

            <div>
              <span className="font-bold">{headerContent.row2.title}:</span> {headerContent.row2.content}
            </div>

            <div>
              <span className="font-bold">{headerContent.row3.title}:</span> {headerContent.row3.content}
            </div>
          </StyledHeaderTextWrapper>

          <StyledHeaderTextWrapper className="w-2/4 pr-4">
            <div>
              <span className="font-bold">{headerContent.row4.title}:</span> {headerContent.row4.content}
            </div>
          </StyledHeaderTextWrapper>
        </div>
      )}
    </>
  );
};

export default TableHeader;

const StyledHeaderTextWrapper = styled.div`
  > div {
    line-height: 1.55;
  }
`;

const StyledButton = styled.button`
  border-color: black;
  border-width: 1px !important;
  border-radius: 0.25rem;
  font-weight: 500 !important;
  color: black !important;
  padding-left: 1rem;
  padding-right: 1rem;
  font-size: 0.75rem;
  height: 1.5rem;
`;
